import React, { useState, useEffect, useLayoutEffect, useRef, Suspense, lazy } from 'react';
import { Typography, Grid, Box, Button, Pagination, Tabs, Tab, Tooltip, IconButton, Snackbar, Menu, MenuItem, ListItemIcon, ListItemText, Divider, CircularProgress, useMediaQuery, useTheme } from '@mui/material';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import FmdGoodOutlinedIcon from '@mui/icons-material/FmdGoodOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import BaseDialog from '../components/dialogs/BaseDialog';
import { updateUserProfile, uploadBanner, deleteBanner, uploadAvatar } from '../services/api';
import { prepareImageUpload } from '../utils/prepareImageUpload';
import { UserProfile } from '../types/UserProfile';
import { useNavigate, useLocation, useParams, useSearchParams, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { SITE_NAME } from '../constants/site';
import { CARD_FX_SCOPE_CLASS } from '../constants/cardHover';
import OnlineStatus from '../components/OnlineStatus';
import GameListComponent from '../components/GameList/GameListComponent';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getUserGameLists, GameListSummary, GameList, deleteGameList } from '../services/contents_api';
import XIcon from '@mui/icons-material/X';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { hasAuthToken } from '../services/auth';
import SettingsIcon from '@mui/icons-material/Settings';
import GetAppIcon from '@mui/icons-material/GetApp';
import Tips from '../components/dialogs/Tips';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { isPwa } from '../utils/isPwa';
import FollowButton from '../components/common/FollowButton';
import StyledButton from '../components/common/StyledButton';
import UserPlanBadge from '../components/common/UserPlanBadge';
import BoltIcon from '@mui/icons-material/Bolt';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SocialLinks, { SAMPLE_SOCIAL_LINKS } from '../components/profile/SocialLinks';
// プラン判定(canUse*)や選択肢の描画は装飾パネル側(ProfileCustomizeSheet)の仕事。
// ここで必要なのは「今どう描くか」を決める resolve と、プラン不足の検出に使う定義だけ。
import {
  ACCENT_PRESETS, ACCENT_PALETTES, DEFAULT_ACCENT, CUSTOM_ACCENT_KEY, accentHex, resolveAccent,
  ACCENT_LEVELS, DEFAULT_ACCENT_LEVEL, AccentLevelKey, resolveAccentLevel, accentReaches,
  CARD_SCORE_ACCENTS, DEFAULT_CARD_SCORE_ACCENT, CardScoreAccentKey, resolveCardScoreAccent,
} from '../constants/profileAccent';
import {
  ProfileAccentLevelContext, ProfileCardScoreAccentContext,
} from '../components/profile/accentScope';
import { SubscriptionPlan, planLabel } from '../utils/subscription';
import { hslOf } from '../utils/color';
import AvatarRing from '../components/profile/AvatarRing';
import BannerGlitch from '../components/profile/BannerGlitch';
import BannerSweep from '../components/profile/BannerSweep';
import BannerTopWash from '../components/profile/BannerTopWash';
import AmbientBackground from '../components/profile/AmbientBackground';
import {
  AMBIENT_PRESETS, DEFAULT_AMBIENT, AmbientKey,
  resolveAmbient, ambientPreset,
} from '../constants/profileAmbient';
import {
  BANNER_EFFECTS, DEFAULT_BANNER_EFFECT, BannerEffectKey,
  resolveBannerEffect, bannerEffectParams, bannerEffectKind, bannerZoomSx,
  GlitchParams, SweepParams, ZoomParams,
} from '../constants/bannerEffect';
import {
  AVATAR_BORDERS, DEFAULT_AVATAR_BORDER, resolveAvatarBorder,
  DEFAULT_AVATAR_BORDER_WIDTH, AvatarBorderWidthKey,
} from '../constants/avatarBorder';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
// サイドレールは一旦取り下げ。載せるコンテンツを決めてから戻す。
// 移設済みのユーティリティ実装は components/profile/MyPageRail.tsx に残してある。
import { getSteamPlayerSummary, getGameIdBySteamId } from '../services/SteamApi';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import { useProfileQuery, useProfileCacheUpdater } from '../hooks/useProfileQuery';
import { useImageReady } from '../hooks/useImageReady';
import { useEdgeFade } from '../hooks/useEdgeFade';
import { useDragScroll } from '../hooks/useDragScroll';
import MediaSegmentedControl from '../components/profile/MediaSegmentedControl';
import ProfileHeaderSkeleton from '../components/profile/ProfileHeaderSkeleton';
import {
  MediaType, MainTabType, MAIN_TABS, MEDIA_COUNT_STATUSES, EXT_BAR_HIDDEN_KEY,
  MEDIA_TAB_STORAGE_KEY, resolveMedia, resolveTab, resolveListPage, isTabAvailable,
  safeLocalGet, safeLocalSet,
} from '../components/profile/profileTabs';
import { ProfileTabLabel, tabCountFor, tabBadgeCount, hasAnyTabBadge } from '../components/profile/ProfileTabLabel';
import { pushAccentHistory } from '../constants/accentCustom';
import {
  LAYOUT_TOP_PAD, banner, AVATAR_SIZE, BANNER_PULL_UP, BANNER_MASK_SX, BANNER_OUTPUT,
  BANNER_ROW_PY, BANNER_ROW_PX, BANNER_BLEED_MX, NEUTRAL_BANNER_BASE, AVATAR_OUTPUT,
  bubbleSurface, GLASS_ACTION_SX, HEADER_ICON_SX, bannerBaseSx, bannerOverlaySx,
} from '../components/profile/headerLayout';

/**
 * タブの中身は**すべて遅延読み込み**。
 *
 * 17個のタブを静的 import していたため、`EditGameList` 経由で Lexical、
 * `GameLibraryTab` 経由で dnd-kit といった重い依存が初期チャンクに乗っていた。
 * 一度に見えるのは1タブだけなので、初期表示に必要なのはヘッダーとタブ列だけ。
 *
 * 追加するときも必ずここへ。直接 import すると初期チャンクへ戻ってしまう。
 */
const GameLibraryTab = lazy(() => import('../components/library/GameLibraryTab'));
const AnimeLibraryTab = lazy(() => import('../components/library/AnimeLibraryTab'));
const MovieLibraryTab = lazy(() => import('../components/library/MovieLibraryTab'));
const ProfileTab = lazy(() => import('../components/ProfileTab'));
const AnimeProfileTab = lazy(() => import('../components/profile/anime/AnimeProfileTab'));
const MovieProfileTab = lazy(() => import('../components/profile/movie/MovieProfileTab'));
const ReviewsTab = lazy(() => import('../components/ReviewsTab'));
const AnimeReviewsTab = lazy(() => import('../components/AnimeReviewsTab'));
const MovieReviewsTab = lazy(() => import('../components/MovieReviewsTab'));
const MemoriesTab = lazy(() => import('../components/MemoriesTab'));
const PostFeed = lazy(() => import('../components/posts/PostFeed'));
const ActivityTab = lazy(() => import('../components/profile/Activity'));
const FollowsTab = lazy(() => import('../components/FollowsTab'));
const TimelineTab = lazy(() => import('../components/TimelineTab'));
const PlayLogsTab = lazy(() => import('../components/PlayLogsTab'));
const GotyTab = lazy(() => import('../components/GotyTab'));
const GalleryTab = lazy(() => import('../components/gallery/GalleryTab'));
const CreateGameList = lazy(() => import('../components/GameList/CreateGameList'));
const EditGameList = lazy(() => import('../components/GameList/EditGameList'));
// 装飾パネル。開くまで要らないので分離する（プレビュー用のスウォッチ群が重い）
const ProfileCustomizeSheet = lazy(() => import('../components/profile/ProfileCustomizeSheet'));

/**
 * タブの読み込み中に置く面。**高さを持たせること。**
 * 0 高さだと、チャンクが届いた瞬間にページが下へ伸びてスクロール位置がズレる。
 */
const TabFallback: React.FC = () => (
  <Box sx={{ minHeight: 320, display: 'flex', justifyContent: 'center', pt: 6 }}>
    <CircularProgress size={24} />
  </Box>
);

// 件数が未取得のときの既定値。**毎回新しい配列を作らないこと**
// (useMemo の依存が毎レンダー変わって再計算になる)。
const EMPTY_COUNTS: number[] = [0, 0, 0, 0, 0];

// 見た目の設定（personalize.d）。下書きと保存値で同じ型を使う
export type DisplayPrefs = NonNullable<UserProfile['personalize']['d']>;
export type DisplayKey = keyof DisplayPrefs;

// キーの順序に左右されずに比較する（下書きは追加・削除で順序が変わるため）
const normalizeDisplay = (d?: DisplayPrefs) =>
  JSON.stringify(Object.entries(d ?? {}).sort());

export interface BlockedDisplayItem {
  key: DisplayKey;
  label: string;
  planLabel: string;
  min: number;
}

interface ProfilePageProps {
  isPublic?: boolean;
  isOwnProfile?: boolean;
}

const ProfilePage: React.FC<ProfilePageProps> = ({
  isPublic = false,
  isOwnProfile = false,
}) => {
  const { t } = useTranslation('profile');
  const navigate = useNavigate();
  const location = useLocation();
  const { username } = useParams<{ username: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();

  // 自分のページはログインが前提。**レンダー中に判定する**。
  // 以前は非同期の isAuthenticated() を effect で待っていたので、未ログインでも
  // 一度ページ全体が描かれ、プロフィールのリクエストまで飛んでいた。
  const authed = hasAuthToken();
  const needsLogin = !isPublic && !authed;

  /**
   * プロフィール本体。**取得口は自分/他人で1本に統一されている。**
   * キャッシュキーがユーザーごとに分かれているので、別人のページへ移った瞬間の
   * profile は必ず undefined になる（前の人が残らない）。
   * 詳細は hooks/useProfileQuery.ts のコメント。
   */
  const { data: profile, isPending, isError } = useProfileQuery(
    isPublic ? username : undefined,
    isPublic ? !!username : authed,
  );
  const profileCache = useProfileCacheUpdater(isPublic ? username : undefined);

  // 件数はプロフィールから導出する。**ローカル state に写さないこと。**
  // 写していた頃は同期漏れで古い件数が残った。
  const gameCounts = profile?.status_counts || EMPTY_COUNTS;
  const animeCounts = profile?.anime_status_counts || EMPTY_COUNTS;
  const movieCounts = profile?.movie_status_counts || EMPTY_COUNTS;

  const [extBarHidden, setExtBarHidden] = useState<boolean>(
    () => safeLocalGet(EXT_BAR_HIDDEN_KEY) === '1'
  );
  const [extInfoOpen, setExtInfoOpen] = useState(false);

  const hasExtData = animeCounts.some(c => c > 0) || movieCounts.some(c => c > 0);
  // 他人のプロフィールでは、相手が使っていないメディアの空タブに意味が無いのでデータがある時だけ出す。
  // 自分のプロフィールでは、拡張ライブラリの存在に気づけるよう常時表示し、不要なら自分で消せる。
  const showMediaBar = isPublic ? hasExtData : (hasExtData || !extBarHidden);

  /**
   * タブとメディアは **URL から導出する**。state に持たない。
   * 以前は state + 「URL へ書く → useEffect で読み戻す」の往復で、
   * 1操作あたり2レンダー走り、初期値の算出が3箇所に重複していた。
   */
  const requestedMedia = resolveMedia(searchParams.get('media'), isPublic);
  // 保険: 切替バーが出ないのに非ゲーム（URL / 端末設定に残った値）だと
  // ゲームライブラリへ戻れなくなる。バーが無い状態では game として扱う。
  const mediaType: MediaType = (!isPending && !showMediaBar) ? 'game' : requestedMedia;
  const mainTabValue = resolveTab(searchParams.get('tab'), mediaType, isPublic);
  const listPage = resolveListPage(searchParams.get('list_page'));

  // 端末に覚えた値も直しておく（次にURL無しで開いたとき同じ迷子にならないように）
  useEffect(() => {
    if (!isPublic && mediaType !== requestedMedia) {
      safeLocalSet(MEDIA_TAB_STORAGE_KEY, mediaType);
    }
  }, [isPublic, mediaType, requestedMedia]);

  /**
   * タブ / メディアを切り替える。**クエリを作り直す（残さない）こと。**
   *
   * `status` `page` `query` `order` `folder` などは**タブごとに意味が違うのに
   * 名前が同じ**。レビューの5ページ目からメモリーへ移ったときに page=5 が
   * 残っていると、メモリーが5ページ目から始まってしまう。
   * ゲーム→アニメでも同じで、ゲームの status や folder はアニメには無い。
   *
   * どのタブがどのパラメータを読むかを列挙して除外する方式にはしないこと。
   * タブを増やすたびに列挙側の更新が要る＝いずれ漏れる。
   * 「新しいタブは自分に必要なものだけ URL に書く」のが前提なので、
   * ここでは tab / media 以外を捨てるのが正しい。
   */
  const goToTab = (tab: MainTabType, media: MediaType, extra?: Record<string, string>) => {
    setSearchParams(new URLSearchParams({ tab, media, ...extra }), { replace: true });
  };

  /**
   * 今のタブの中でのパラメータ変更（ページ送りなど）。
   * こちらは**今のタブが読んでいる値を消してはいけない**ので既存を残す。
   */
  const patchSearchParams = (patch: Record<string, string>) => {
    const params = new URLSearchParams(searchParams);
    Object.entries(patch).forEach(([k, v]) => params.set(k, v));
    setSearchParams(params, { replace: true });
  };

  const [headerMenuAnchor, setHeaderMenuAnchor] = useState<null | HTMLElement>(null);
  const [shareMenuAnchor, setShareMenuAnchor] = useState<null | HTMLElement>(null);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [createListDialogOpen, setCreateListDialogOpen] = useState(false);
  // 一度でも開いたか。**開いた後は閉じてもマウントしたまま**にする。
  // 遅延読み込みのために「開いている間だけマウント」にすると、閉じた瞬間に
  // 消えて閉じるアニメーションが出ない（元は常時マウントだった）。
  // この2段構えなら、初期チャンクから外したまま元の見え方を保てる。
  const [createListMounted, setCreateListMounted] = useState(false);
  const openCreateList = () => {
    setCreateListMounted(true);
    setCreateListDialogOpen(true);
  };
  const [editingList, setEditingList] = useState<GameList | null>(null);

  const notify = (message: string) => {
    setSnackbarMessage(message);
    setOpenSnackbar(true);
  };

  const scrollPositionRef = useRef<number>(0);

  // メインタブの横スクロール。MUIの矢印(TabScrollButton)は無効時も幅を占有して
  // 端に死んだ余白を作るため使わず、スクロール可能な側だけをフェードさせて示す。
  // ドラッグでの横移動も同じ要素に効かせる（PC は矢印が無いぶんの代替）。
  const tabsBoxRef = useRef<HTMLDivElement>(null);
  const resolveTabScroller = () =>
    tabsBoxRef.current?.querySelector<HTMLDivElement>('.MuiTabs-scroller');
  // タブ列が作り替わるのは メディア / 立場 が変わったとき。
  // **プロフィールの有無も必ず含めること。** タブ列はプロフィールが確定してから
  // 描かれるので、これが無いと「まだ存在しない要素」に対して一度だけ実行して
  // 諦めたまま二度と張り直さない（＝端のフェードもドラッグも死ぬ）。
  const tabScrollerRevision = `${mediaType}:${isPublic}:${!!profile}`;
  const tabFadeMask = useEdgeFade(resolveTabScroller, tabScrollerRevision);
  useDragScroll(resolveTabScroller, tabScrollerRevision);

  // スクロール位置を保存（タブ切替で位置を保つのに使う）
  useEffect(() => {
    const handleScroll = () => {
      scrollPositionRef.current = window.scrollY;
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  /**
   * 【ユーザーが変わったときのスクロール位置は**ここで触らないこと**】
   *
   * 「別のユーザーへ移ったら先頭へ戻す」を `username` の変化を見る useEffect で
   * やっていたが、これはスクロール位置の復元を壊す。消した理由を残す。
   *
   * `/profile/:username` は**同じルート**なので、A さん → B さんではこのページは
   * アンマウントされない。`username` だけが変わる。そして戻るボタンで A さんへ
   * 帰ってきたときも、起きるのは同じ「`username` の変化」でしかない。
   * つまり effect 側からは「別人へ移った」と「戻ってきた」が区別できず、
   * ScrollRestoration が復元した位置を直後に先頭へ上書きしてしまっていた。
   * (自分のページは `username` が常に undefined なので発火せず、
   *  他人のプロフィールでだけ位置が保存されない形で出る)
   *
   * そもそも不要でもある。PUSH 遷移で先頭へ戻すのは ScrollRestoration の既定動作で、
   * `preventScrollReset` を使っている箇所はアプリ内に無い。
   * タブの初期化も URL 由来なので、`?tab=` の無い URL へ移れば自動で既定へ戻る。
   */

  /**
   * Steam のプレイ中ステータス。
   * 以前は素の useEffect + setState だったので、ユーザー間を素早く移ると
   * 前の人のレスポンスが後から届いて表示を上書きすることがあった。
   */
  const { data: steamStatus } = useQuery({
    queryKey: ['steamPlayerSummary', profile?.steam_id],
    queryFn: () => getSteamPlayerSummary(profile!.steam_id!),
    enabled: !!profile?.steam_id,
    staleTime: 60_000,
  });

  const handleHideExtBar = () => {
    setExtBarHidden(true);
    safeLocalSet(EXT_BAR_HIDDEN_KEY, '1');
    setExtInfoOpen(false);
  };

  // 紹介文は 3 行で切って、全文はダイアログで開かせる。
  // 「実際に切れているか」は文字数では判定できない(幅で変わる)ので実測する。
  // ResizeObserver で幅の変化にも追従させること。文字数の目安で代用すると、
  // 切れていないのに「続きを読む」が出る状態が普通に起きる。
  const bioRef = useRef<HTMLDivElement>(null);
  const [bioTruncated, setBioTruncated] = useState(false);
  useLayoutEffect(() => {
    const el = bioRef.current;
    if (!el) {
      setBioTruncated(false);
      return;
    }
    const check = () => setBioTruncated(el.scrollHeight > el.clientHeight + 1);
    check();
    const observer = new ResizeObserver(check);
    observer.observe(el);
    // Web フォントの適用で行の高さが変わると溢れ方が変わる
    document.fonts?.ready.then(check).catch(() => {});
    return () => observer.disconnect();
  }, [profile?.bio]);

  // 場所タグは SOCIAL の右という可変幅の隙間に入るので、狭いと普通に切れる。
  // 切れているときだけ全文をダイアログで開かせる。判定は紹介文と同じ考え方だが、
  // こちらは1行なので横(scrollWidth)で見る。
  const locationRef = useRef<HTMLElement>(null);
  const [locationTruncated, setLocationTruncated] = useState(false);
  useLayoutEffect(() => {
    const el = locationRef.current;
    if (!el) {
      setLocationTruncated(false);
      return;
    }
    const check = () => setLocationTruncated(el.scrollWidth > el.clientWidth + 1);
    check();
    const observer = new ResizeObserver(check);
    observer.observe(el);
    document.fonts?.ready.then(check).catch(() => {});
    return () => observer.disconnect();
  }, [profile?.location]);

  // 下端行の右カラム(フォロー / 所有者アイコン列)の幅。名前行をこのぶん右へ伸ばす。
  // 文言はロケールと状態で変わり、狭い画面ではアイコンのみになるので実測すること。
  const bannerActionsRef = useRef<HTMLDivElement>(null);
  const [bannerActionsWidth, setBannerActionsWidth] = useState(0);
  useLayoutEffect(() => {
    const el = bannerActionsRef.current;
    if (!el) {
      setBannerActionsWidth(0);
      return;
    }
    // 同じ値で setState すると RO → render → RO の往復になるので、変化時だけ通す
    const measure = () => setBannerActionsWidth(prev => (prev === el.offsetWidth ? prev : el.offsetWidth));
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    document.fonts?.ready.then(measure).catch(() => {});
    return () => observer.disconnect();
  }, [profile]);

  // 紹介文があるときは常に Tips で包む。
  // bioTruncated で包むかどうかを切り替えると、false→true の瞬間に要素の型が
  // Box から Tips に変わって吹き出しごと作り直され、測定対象の DOM が差し替わって
  // ResizeObserver が外れた要素を監視し続ける。ツリーを固定して事故を無くす。
  // 3行に収まっている場合は「続きを読む」もカーソル変化も出ないので、
  // 開けること自体はユーザーからは見えない。
  const wrapWithBioDialog = (bubble: React.ReactElement) => (
    profile?.bio ? (
      <Tips
        title="自己紹介"
        maxWidth="sm"
        buttonComponent={bubble}
        content={
          <Typography variant="body2" sx={{ lineHeight: 1.8, whiteSpace: 'pre-wrap' }}>
            {profile.bio}
          </Typography>
        }
      />
    ) : bubble
  );

  // 紹介文と同じ理由でツリーを固定する。切れているかで包む/包まないを切り替えると
  // 測定対象の DOM が差し替わって ResizeObserver が外れた要素を見続ける。
  const wrapWithLocationDialog = (tag: React.ReactElement) => (
    profile?.location ? (
      <Tips
        title="場所"
        buttonComponent={tag}
        // このラッパーが flex アイテムになる。縮まないと中身の省略が効かない。
        buttonSx={{ display: 'flex', minWidth: 0 }}
        content={
          <Typography variant="body2" sx={{ lineHeight: 1.8, whiteSpace: 'pre-wrap' }}>
            {profile.location}
          </Typography>
        }
      />
    ) : tag
  );

  // ヘッダー画像。導線は「…」メニューに集約し、実体は隠した file input。
  const bannerInputRef = useRef<HTMLInputElement>(null);
  const [bannerUploading, setBannerUploading] = useState(false);

  // 選んだ直後はアップロードせず、実際のヘッダーに当ててみて保存/キャンセルを選ばせる。
  // クライアント側でサーバーと同じ 1280x540 に切ってからプレビューするので、
  // ここで見えているものと保存後の見え方は一致する。
  const [pendingBanner, setPendingBanner] = useState<{ url: string; blob: Blob; filename: string } | null>(null);

  // objectURL は明示的に開放しないとページを離れるまで残る
  useEffect(() => () => {
    if (pendingBanner) URL.revokeObjectURL(pendingBanner.url);
  }, [pendingBanner]);

  const handleBannerFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // 同じファイルを選び直しても change が飛ぶようにリセットしておく
    e.target.value = '';
    if (!file) return;

    setBannerUploading(true);
    try {
      const prepared = await prepareImageUpload(file, {
        cropTo: { width: BANNER_OUTPUT.width, height: BANNER_OUTPUT.height },
        quality: 0.85,
      });
      if (!prepared.processed) {
        // HEIC など canvas で扱えない形式。プレビューを出せないので選び直してもらう
        notify('この形式はプレビューできません。JPEG / PNG / WebP を選んでください');
        return;
      }
      // 直前の objectURL の開放は上の useEffect のクリーンアップに任せる
      // (setState の更新関数の中で副作用を起こすと StrictMode で二重に走る)
      setPendingBanner({
        url: URL.createObjectURL(prepared.blob),
        blob: prepared.blob,
        filename: prepared.filename,
      });
    } catch {
      notify('画像の読み込みに失敗しました');
    } finally {
      setBannerUploading(false);
    }
  };

  const handleBannerCancel = () => setPendingBanner(null);

  /**
   * アイコン。**保存経路はプロフィール本体と独立している**
   * （uploadAvatar は専用エンドポイントで、誕生日必須などプロフィール設定フォームの
   * 制約を一切通らない）。だから装飾パネルの中から直接差し替えられる。
   *
   * 手順はヘッダー画像と同じにしてある。選んだ直後は送らず、実物に当てて
   * 保存/キャンセルを選ばせる。**片方だけ違う操作にしないこと。**
   */
  const avatarInputRef = useRef<HTMLInputElement>(null);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [pendingAvatar, setPendingAvatar] = useState<{ url: string; blob: Blob; filename: string } | null>(null);

  useEffect(() => () => {
    if (pendingAvatar) URL.revokeObjectURL(pendingAvatar.url);
  }, [pendingAvatar]);

  const handleAvatarFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    setAvatarUploading(true);
    try {
      // **forceJpeg が要る。** アイコンの受け口は JPEG / PNG しか許可していない
      // （既定の WebP で送るとサーバーに弾かれる）。
      const prepared = await prepareImageUpload(file, {
        cropTo: { width: AVATAR_OUTPUT.width, height: AVATAR_OUTPUT.height },
        quality: 0.85,
        forceJpeg: true,
      });
      if (!prepared.processed) {
        notify('この形式はプレビューできません。JPEG / PNG を選んでください');
        return;
      }
      setPendingAvatar({
        url: URL.createObjectURL(prepared.blob),
        blob: prepared.blob,
        filename: prepared.filename,
      });
    } catch {
      notify('画像の読み込みに失敗しました');
    } finally {
      setAvatarUploading(false);
    }
  };

  const handleAvatarCancel = () => setPendingAvatar(null);

  const handleBannerSave = async () => {
    if (!pendingBanner) return;
    setBannerUploading(true);
    try {
      const formData = new FormData();
      formData.append('banner', pendingBanner.blob, pendingBanner.filename);
      const { banner: uploaded } = await uploadBanner(formData);
      // 表示はキャッシュが正。ローカル state に写さない
      profileCache.patch({ banner: uploaded });
      handleBannerCancel();
      setSnackbarMessage('ヘッダー画像を更新しました');
    } catch (error: any) {
      setSnackbarMessage(error?.response?.data?.error || 'ヘッダー画像の更新に失敗しました');
    } finally {
      setBannerUploading(false);
      setOpenSnackbar(true);
    }
  };

  const handleBannerDelete = async () => {
    setHeaderMenuAnchor(null);
    setBannerUploading(true);
    try {
      await deleteBanner();
      profileCache.patch({ banner: null });
      setSnackbarMessage('ヘッダー画像を削除しました');
    } catch {
      setSnackbarMessage('ヘッダー画像の削除に失敗しました');
    } finally {
      setBannerUploading(false);
      setOpenSnackbar(true);
    }
  };

  // 判定はプロフィール所有者のプラン（閲覧者のプランではない）
  const ownerPlan = profile?.subscription_plan ?? 0;

  // 見た目の設定は**下書きで持つ**。パネルを開いている間はこちらが正で、
  // 「保存」を押すまでサーバーへ送らない。理由はパネル側のコメントを参照。
  const savedDisplay = profile?.personalize?.d;
  const [draftDisplay, setDraftDisplay] = useState<DisplayPrefs | null>(null);
  const [displaySaving, setDisplaySaving] = useState(false);
  // 描画は必ずこれを見ること。保存済みの値を直接読むとプレビューが効かない
  const display = draftDisplay ?? savedDisplay;

  // **試着中はプラン判定を通さない。** 実際に自分のアバターへ乗った状態を見せるため。
  // 保存済みの値は必ず所有者のプランで resolve する（＝解約者のPRO装飾が他人に見えない）。
  // この2経路を混ぜないこと。混ぜるとどちらかの事故になる。
  const gatePlan = draftDisplay != null ? SubscriptionPlan.MAX : ownerPlan;

  // プラン不足で保存できない選択。保存対象から外し、訴求ストリップに出す
  const blockedDisplay = React.useMemo<BlockedDisplayItem[]>(() => {
    if (!draftDisplay) return [];
    const need = (key: DisplayKey, label: string, min: number | undefined) =>
      min && min > ownerPlan ? [{ key, label, planLabel: planLabel(min), min }] : [];
    // カスタムはプリセット表に無いので、key から直接パレットを決める
    const accentPalette = draftDisplay.ac === CUSTOM_ACCENT_KEY
      ? 'custom'
      : ACCENT_PRESETS.find(p => p.key === draftDisplay.ac)?.palette;
    return [
      // ラベルは**設定の名前**で揃える。プリセットの固有名(ホロ等)を混ぜると
      // 1項目だけ粒度が細かくなって並びが不揃いに見える
      ...need('ac', 'アクセントカラー', ACCENT_PALETTES.find(p => p.key === accentPalette)?.minPlan),
      ...need('al', 'アクセントスタイル', ACCENT_LEVELS.find(l => l.key === draftDisplay.al)?.minPlan),
      ...need('cs', 'カードのスコア', CARD_SCORE_ACCENTS.find(o => o.key === draftDisplay.cs)?.minPlan),
      ...need('ab', 'アイコンの縁取り', AVATAR_BORDERS.find(b => b.key === draftDisplay.ab)?.minPlan),
      ...need('be', 'ヘッダーの効果', BANNER_EFFECTS.find(e => e.key === draftDisplay.be)?.minPlan),
      ...need('bg', 'ページの背景', AMBIENT_PRESETS.find(a => a.key === draftDisplay.bg)?.minPlan),
    ];
  }, [draftDisplay, ownerPlan]);

  // 保存できるぶんだけ（保存ボタンは止めない）。
  // プラン不足の項目は**削除ではなく保存済みの値へ戻す**こと。
  // 削除にすると、解約したユーザーがパネルを開いて保存しただけで
  // 契約中に保存した装飾が永久に消える。「保存値は消さない（再契約で戻る）」方針に反する。
  const savableDisplay = React.useMemo(() => {
    if (!draftDisplay) return null;
    const next = { ...draftDisplay };
    blockedDisplay.forEach(b => {
      const saved = savedDisplay?.[b.key];
      if (saved === undefined) delete next[b.key];
      else (next as Record<string, string>)[b.key] = saved;
      // acx は ac に従属する。ac を戻すときに置き去りにすると、
      // 使われない色だけが保存値に残る
      if (b.key === 'ac') {
        if (savedDisplay?.acx === undefined) delete next.acx;
        else next.acx = savedDisplay.acx;
      }
    });
    return next;
  }, [draftDisplay, blockedDisplay, savedDisplay]);

  const displayDirty =
    savableDisplay != null && normalizeDisplay(savableDisplay) !== normalizeDisplay(savedDisplay);

  const muiTheme = useTheme();
  const [customizeOpen, setCustomizeOpen] = useState(false);

  const openCustomize = () => {
    setDraftDisplay({ ...(savedDisplay ?? {}) });
    setCustomizeOpen(true);
  };
  // 閉じる＝破棄。試すだけの操作を残さないための仕様。
  // 選択中のアイコン画像も同じ扱い（パネルを閉じたのにプレビューだけ残ると、
  // 保存する手段が無いまま元と違う絵が出続ける）
  const closeCustomize = () => {
    setDraftDisplay(null);
    setPendingAvatar(null);
    setCustomizeOpen(false);
  };
  const resetDisplay = () => setDraftDisplay({ ...(savedDisplay ?? {}) });
  // 既定＝キーを1つも持たない状態。個々の既定値を書き込むのではなく空にすること
  // （既定値を明示保存すると、後で既定を変えたときに追従しなくなる）
  const resetDisplayToDefault = () => setDraftDisplay({});

  /**
   * パネルの保存。**アイコン画像もここでまとめて確定する。**
   *
   * アイコンに専用の保存ボタンを持たせない理由: パネルの中にもう一つ保存が
   * 増えると、どちらを押せば何が保存されるのか分からなくなる。
   * 押す場所は「パネルの保存」1つに寄せ、アイコン側は選ぶ/取り消すだけにする。
   *
   * 送り先は別（アイコンは専用エンドポイント、装飾は personalize の PATCH）なので、
   * **片方が失敗しても片方は通る。** 順番はアイコンを先にして、
   * 失敗したらそこで止める（装飾だけ保存されて画像が入っていない状態を作らない）。
   */
  const saveDisplay = async () => {
    if (!profile || !savableDisplay) return;
    const personalize = { ...profile.personalize };
    // 既定値だけになったらキーごと落とす（保存値を最小に保つ）
    if (Object.keys(savableDisplay).length === 0) delete personalize.d;
    else personalize.d = savableDisplay;
    setDisplaySaving(true);
    try {
      if (pendingAvatar) {
        const formData = new FormData();
        formData.append('avatar', pendingAvatar.blob, pendingAvatar.filename);
        const { avatar_url: uploaded } = await uploadAvatar(formData);
        // 表示はキャッシュが正。ローカル state に写さない
        profileCache.patch({ avatar: uploaded });
        setPendingAvatar(null);
      }
      await updateUserProfile({ personalize });
      profileCache.patch({ personalize });
      /*
       * 色履歴は**保存が通ったときだけ**積む。
       * スライダーを離すたびに積んでいた頃は、色を決めるまでの過程が全部残って
       * 「最近使った色」が試行錯誤のログになっていた。履歴に意味があるのは
       * 「実際に自分のプロフィールに適用した色」だけ。
       */
      if (savableDisplay.ac === CUSTOM_ACCENT_KEY && savableDisplay.acx) {
        pushAccentHistory(savableDisplay.acx);
      }
      // 下書きは残す。試着中のPRO項目を保存後も見せ続けるため
      setSnackbarMessage('表示設定を保存しました');
    } catch (error: any) {
      console.error('表示設定の更新に失敗しました', error);
      // アイコン側の拒否理由（サイズ/形式）はそのまま伝える。
      // 「保存に失敗しました」だけだと何を直せばよいか分からない
      setSnackbarMessage(
        error?.response?.data?.avatar?.[0] || error?.response?.data?.error || '設定の保存に失敗しました'
      );
    } finally {
      setDisplaySaving(false);
      setOpenSnackbar(true);
    }
  };

  /**
   * アクセント色。プロフィール配下だけ primary を差し替える。
   * こうすると既存の `primary.main` 参照が全部そのまま追従するので、
   * 各コンポーネントを書き換える必要がない。
   * 対象を広げたくなったら、この ThemeProvider の位置を外へ動かすだけでよい。
   * 保存値ではなく resolve 後の値を使うこと。有料の装飾は解約で無効に戻る。
   * 判定はプロフィール所有者のプラン（閲覧者のプランではない）。
   */
  const accentKey = resolveAccent(display?.ac, gatePlan);
  // アクセントをどこまで広げるか。要素側は accentReaches でしか判定しないこと
  const accentLevel = resolveAccentLevel(display?.al, gatePlan);
  const accentBold = accentReaches(accentLevel, 'b');
  // カードのスコアの星。段階とは独立した ON/OFF
  const cardScoreAccent = resolveCardScoreAccent(display?.cs, gatePlan);

  // ページ全体の環境光。静止。preset は定義済みオブジェクトなので参照は安定している
  const ambientKey = resolveAmbient(display?.bg, gatePlan);
  // **プロフィールが確定するまで環境光は敷かない。**
  // 敷いてしまうと既定プリセットが一度出てから本人の設定へ切り替わる。
  const ambient = profile ? ambientPreset(ambientKey) : null;
  // カスタムのときだけ acx の色を使う。**生の保存値を渡してよい**
  // （accentHex の中で形式とコントラストを検証する。personalize はサーバー検証が無い）
  const accent = accentHex(accentKey, display?.acx);
  const profileTheme = React.useMemo(
    () => createTheme(muiTheme, { palette: { primary: { main: accent } } }),
    [muiTheme, accent]
  );

  // アバターの縁取り。リングはアクセント色を材料にするので accent と対で使う。
  // 太さは無料なので resolve しない。
  const avatarBorder = resolveAvatarBorder(display?.ab, gatePlan);
  const avatarBorderWidth: AvatarBorderWidthKey =
    display?.aw || DEFAULT_AVATAR_BORDER_WIDTH;

  // ヘッダーの効果。画像が無いときは出さない（ずらす絵・光らせる絵が無い）。
  const bannerImageUrl = pendingBanner?.url || profile?.banner || null;
  // 画像未設定時の色。**アクセントを明示的に選んでいる人だけ**その色相に合わせる。
  // display.ac が無い＝既定のまま＝未設定。プラン落ちで既定へ戻された場合も
  // resolve 後と一致しなくなるので、実際に描かれている色とズレない。
  const bannerAccentHue =
    display?.ac && accentKey === display.ac ? hslOf(accent).h : undefined;

  /**
   * バナーの下地。
   *
   * **読み込み中は誰のものでもない無彩色を敷き、確定したらフェードで重ねる。**
   *
   * 以前は読み込み中もユーザー名シードのグラデを描いていたが、そのときのシードは
   * 空文字なので色相が 0(赤)に固定され、データ到着時に本人の色相へ飛んでいた。
   * SP は BannerTopWash が読み込み中も出るので、その飛びが画面最上部にも見えていた。
   *
   * 画像はデコードが終わるまで見せない。取得直後から描かせると大きな絵が
   * 上から順に出たり、スクリムとの合成が食い違って帯に見えたりする。
   * **要素自体は先に置いておくこと**（opacity 0 で待つ）。後から差し込むと
   * そこで初めて取得が始まって、待った意味が無くなる。
   */
  const bannerImageReady = useImageReady(bannerImageUrl);
  // 本人の絵。profile が無いあいだは描く材料が無いので無彩色のまま
  const bannerPaintedBase = profile
    ? bannerBaseSx(bannerImageUrl, profile.username, bannerAccentHue)
    : NEUTRAL_BANNER_BASE;
  const bannerRevealed = !!profile && bannerImageReady;

  const bannerEffect = resolveBannerEffect(display?.be, gatePlan);
  // params は毎レンダーで新しい参照にすると BannerGlitch が作り直しになる
  const bannerEffectConfig = React.useMemo(() => bannerEffectParams(bannerEffect), [bannerEffect]);
  const bannerKind = bannerEffectKind(bannerEffect);
  // 実際に効果が動く条件。周囲のレイヤー設定もこれで揃える
  const bannerAnimating = !!bannerEffectConfig && !!bannerImageUrl && bannerRevealed;

  // 下書きを1つ書き換えるだけ。送信は saveDisplay() でまとめて行う。
  // 既定値はキーを持たせない。ただし **キー単位で消すこと**。
  // オブジェクトごと捨てると、片方を既定に戻したときにもう片方の設定まで巻き添えで消える。
  const setDisplaySetting = (key: DisplayKey, value: string, isDefault: boolean) =>
    setDraftDisplay(prev => {
      const next = { ...(prev ?? savedDisplay ?? {}) };
      if (isDefault) delete next[key];
      else (next as Record<string, string>)[key] = value;
      return next;
    });

  /**
   * プリセットを選ぶ。**カスタム色(acx)は道連れに消すこと。**
   * 残すと、他が全部既定でも acx だけ残って「既定に戻っていない」判定になる
   * （既定は「キーを1つも持たない」で表現している）。
   */
  const handleAccentChange = (next: string) =>
    setDraftDisplay(prev => {
      const d = { ...(prev ?? savedDisplay ?? {}) };
      delete d.acx;
      if (next === DEFAULT_ACCENT) delete d.ac;
      else d.ac = next;
      return d;
    });

  /**
   * カスタム色。**ac と acx を1回の更新で両方動かすこと。**
   * 別々に呼ぶと ac='custom' なのに acx がまだ古い、という中間状態が1レンダー挟まり、
   * 色が一瞬前の値へ飛ぶ。
   */
  const handleAccentCustomChange = (hex: string) =>
    setDraftDisplay(prev => ({
      ...(prev ?? savedDisplay ?? {}),
      ac: CUSTOM_ACCENT_KEY,
      acx: hex,
    }));
  const handleAccentLevelChange = (next: AccentLevelKey) =>
    setDisplaySetting('al', next, next === DEFAULT_ACCENT_LEVEL);
  const handleCardScoreAccentChange = (next: CardScoreAccentKey) =>
    setDisplaySetting('cs', next, next === DEFAULT_CARD_SCORE_ACCENT);
  const handleAmbientChange = (next: AmbientKey) =>
    setDisplaySetting('bg', next, next === DEFAULT_AMBIENT);
  const handleAvatarBorderChange = (next: string) =>
    setDisplaySetting('ab', next, next === DEFAULT_AVATAR_BORDER);
  const handleAvatarBorderWidthChange = (next: AvatarBorderWidthKey) =>
    setDisplaySetting('aw', next, next === DEFAULT_AVATAR_BORDER_WIDTH);
  const handleBannerEffectChange = (next: BannerEffectKey) =>
    setDisplaySetting('be', next, next === DEFAULT_BANNER_EFFECT);

  // バナー下端行は 375px 幅だと アバター(84) + 通常表示の FollowButton(約230) で
  // 名前カラムが 30px 弱まで潰れる。位置をずらすのではなく、狭いときは
  // FollowButton と SocialLinks をアイコンだけにして幅の要求そのものを下げる。
  const isNarrowHeader = useMediaQuery(muiTheme.breakpoints.down('md'));
  // バナーを AppBar の下へ潜り込ませない側。**Header の isMobile と同じ境界にすること。**
  // ここがズレると、透過した AppBar の裏に何も無い帯が出る。
  const isCompactHeader = useMediaQuery(muiTheme.breakpoints.down('sm'));

  const hasSocialLinks = !!profile?.social_links && Object.keys(profile.social_links).length > 0;
  /**
   * 装飾を試着している最中か。**空の要素を見本で埋める条件。**
   *
   * 「アクセントスタイル」が色を乗せる先は 紹介文の面 / SNSアイコン / タブの件数 で、
   * どれも登録したての人には存在しない。空のまま出すと、切り替えても画面が
   * 一切変わらず設定が壊れているようにしか見えないので、試着中だけ見本を置く。
   * **本物がある要素には出さないこと**（見本と実物が並ぶと区別がつかない）。
   */
  const previewEmpty = !isPublic && customizeOpen;
  const showSampleSocial = previewEmpty && !hasSocialLinks;
  // タブの件数は**1つも数字が出ない人にだけ**見本を出す。
  // 実数を持っている人に 0 件のタブだけ 99 を混ぜると、同じ行に本物と見本が
  // 並んで区別がつかない（hasAnyTabBadge のコメント参照）。
  const showSampleTabCounts = previewEmpty && !hasAnyTabBadge(mediaType, profile);

  // 切替バーに出す件数。
  // 積みゲー(4)とウィッシュリスト(5)は「まだ触れていないもの」なので数えない。
  // 意味のあるステータスはメディアごとに違う(映画に「視聴中」「中断」はほぼ無い)。
  const mediaCounts = React.useMemo(() => {
    const sum = (counts: number[], statuses: number[]) =>
      statuses.reduce((total, s) => total + (counts[s] || 0), 0);
    return {
      game: sum(gameCounts, MEDIA_COUNT_STATUSES.game),
      anime: sum(animeCounts, MEDIA_COUNT_STATUSES.anime),
      movie: sum(movieCounts, MEDIA_COUNT_STATUSES.movie),
    };
  }, [gameCounts, animeCounts, movieCounts]);

  /**
   * 自分のプロフィールのユーティリティ群（見た目 / 共有 / 設定 / その他）。
   * 使用頻度が高いのでメニューには畳まない(畳むと常に2クリックになる)。
   *
   * **置き場所が PC と SP で違う。**
   * - PC: 下端行の右端（従来どおり）。バナーが AppBar の裏へ潜り込んでいる
   *   (BANNER_PULL_UP)ため、右上には置けない＝隠れて押せなくなる。
   * - SP: バナーの右上へ絶対配置。下端行に置くと アバター(72固定) と合わせて
   *   幅を先取りし、375px で名前カラムが 139px まで潰れて 名前 / PROバッジ /
   *   状態チップ が総崩れになる。抜けば 259px 使えて折り返しが起きない。
   */
  const ownerActions = (
    <>
      <Tooltip title="表示のカスタマイズ">
        <IconButton
          size="small"
          aria-label="表示のカスタマイズ"
          onClick={openCustomize}
          sx={HEADER_ICON_SX}
        >
          <PaletteOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="共有">
        <IconButton
          size="small"
          aria-label="共有"
          onClick={(e) => setShareMenuAnchor(e.currentTarget)}
          sx={HEADER_ICON_SX}
        >
          <ShareOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title={t('settingsButton')}>
        <IconButton
          size="small"
          aria-label={t('settingsButton')}
          onClick={() => navigate('/profile/settings')}
          sx={HEADER_ICON_SX}
        >
          <SettingsIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <IconButton
        size="small"
        aria-label="プロフィールの操作"
        onClick={(e) => setHeaderMenuAnchor(e.currentTarget)}
        sx={HEADER_ICON_SX}
      >
        <MoreHorizIcon fontSize="small" />
      </IconButton>
    </>
  );

  // ユーティリティ群を下端行に置くか。SP は置かない(バナー右上へ)。
  const ownerActionsInBottomRow = !isCompactHeader;

  // 下端行の右端。フォローは相手プロフィールの主導線なので、SP でも
  // 右上へは動かさず名前の隣に残す（X と同じ考え方）。
  const bannerActions = profile ? (
    <Box ref={bannerActionsRef} sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, md: 1 }, flexShrink: 0 }}>
      {isPublic ? (
        <FollowButton
          username={profile.username}
          isFollowing={profile.is_following}
          isOwnProfile={isOwnProfile}
          isFollowedBy={profile.is_followed_by}
          iconOnly={isNarrowHeader}
          iconSx={HEADER_ICON_SX}
          onFollowChange={(isFollowing) => {
            // 楽観更新もキャッシュへ。表示はキャッシュしか見ていない。
            // フォロータブのバッジは相手のフォロワー数を含むので、ここも合わせる
            // (以前は応答に存在しない followers_count を足していて NaN になっていた)
            profileCache.update(prev => ({
              ...prev,
              is_following: isFollowing,
              tab_counts: prev.tab_counts && {
                ...prev.tab_counts,
                followers: prev.tab_counts.followers + (isFollowing ? 1 : -1),
              },
            }));
          }}
        />
      ) : ownerActionsInBottomRow ? ownerActions : null}
    </Box>
  ) : null;

  // 共有はバナー上のメニューを MUI Menu に統一したいのでここで実装している。
  // 共通コンポーネントの ShareButtons は独自の Button + PopoverMenu で
  // 形も中身も MUI Menu と揃わないため、バナー上では使わない。
  // 挙動(X の intent URL / クリップボード)は ShareButtons と同じに保つこと。
  const profileShareUrl = (p: UserProfile) => `${process.env.REACT_APP_FRONTEND_URL}/profile/${p.username}`;

  // ゲストのプロフィールは公開されないので共有させず、登録促しに倒す
  const blockedAsGuest = (p: UserProfile) => {
    if (!p.is_guest) return false;
    window.dispatchEvent(new CustomEvent('guestUpgradeRequired'));
    return true;
  };

  const handleShareX = (p: UserProfile) => {
    setHeaderMenuAnchor(null);
    if (blockedAsGuest(p)) return;
    const text = `${p.display_name || p.username}のゲームライブラリ | ${SITE_NAME} - ゲーム記録管理プラットフォーム`;
    const params = `text=${encodeURIComponent(text)}&url=${encodeURIComponent(profileShareUrl(p))}&hashtags=${encodeURIComponent('PULSE_DB')}`;
    window.open(`https://twitter.com/intent/tweet?${params}`, '_blank');
  };

  const handleCopyProfileUrl = (p: UserProfile) => {
    setHeaderMenuAnchor(null);
    if (blockedAsGuest(p)) return;
    navigator.clipboard.writeText(profileShareUrl(p));
    notify('URLをコピーしました');
  };

  const handleSteamGameClick = async () => {
    if (!steamStatus?.gameid) return;

    // PULSEのgame_idを取得
    const pulseGameId = await getGameIdBySteamId(steamStatus.gameid);
    if (pulseGameId) {
      navigate(`/game/${pulseGameId}`);
    } else {
      notify('データベースに登録されていないか、PULSEとSteamの連携が完了していないゲームです');
    }
  };

  /**
   * メインタブの切替。
   *
   * URL が正なので、ここは URL を書き換えるだけ。
   * スクロール位置は明示的に戻す（クエリの変化で先頭へ飛ばされるのを打ち消す）。
   */
  const handleMainTabChange = (event: React.SyntheticEvent, newValue: MainTabType) => {
    const currentScroll = scrollPositionRef.current;
    // リストのページだけは引き継ぐ（元の実装と同じ。他のタブのページ番号とは
    // 名前が違う専用パラメータなので混ざらない）
    goToTab(newValue, mediaType,
      newValue === MAIN_TABS.LISTS ? { list_page: String(listPage) } : undefined);

    requestAnimationFrame(() => {
      window.scrollTo(0, currentScroll);
    });

    const target = event.currentTarget as HTMLElement;
    if (target) {
      setTimeout(() => {
        target.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }, 0);
    }
  };

  const handleMediaChange = (_event: React.SyntheticEvent, newMedia: MediaType) => {
    if (!isPublic) safeLocalSet(MEDIA_TAB_STORAGE_KEY, newMedia);

    // 公開プロフィールはメディアを切り替えたら必ずプロフィールタブへ戻す。
    // 他人のページでメディアだけ変えたいときは「その人のアニメを見たい」であって
    // 「今開いているライブラリをアニメで見たい」ではない。タブを引き継ぐと
    // 前のメディアの文脈が残ってしまう。
    //
    // 自分のページはタブを引き継ぐ（作業の続きをそのメディアで続けたいため）。
    // ただし対象メディアに無いタブだけはライブラリへ落とす（resolveTab の仕事）。
    const nextTab = isPublic
      ? MAIN_TABS.PROFILE
      : resolveTab(mainTabValue, newMedia, isPublic);

    goToTab(nextTab, newMedia);
  };

  // React Query でゲームリストを取得
  const stableListUsername = isPublic ? username : 'me';
  const { data: gameListsData } = useQuery({
    queryKey: ['gameLists', stableListUsername, listPage],
    queryFn: () => getUserGameLists(isPublic ? username : undefined, listPage),
    enabled: mainTabValue === MAIN_TABS.LISTS,
    staleTime: isPublic ? 30000 : 2000,
  });

  const gameLists: GameListSummary[] = gameListsData?.results || [];
  const listTotalPages = Math.ceil((gameListsData?.count || 0) / 12);

  const handleEditList = (list: GameListSummary) => {
    setEditingList(list as unknown as GameList); // 一時的な型キャスト
  };

  const invalidateGameLists = () => {
    queryClient.invalidateQueries({ queryKey: ['gameLists', stableListUsername] });
  };

  const handleDeleteList = async (listId: number) => {
    if (window.confirm('このリストを削除してもよろしいですか？')) {
      try {
        await deleteGameList(listId);
        invalidateGameLists();
      } catch (error) {
        console.error('リストの削除に失敗しました:', error);
      }
    }
  };

  const handleListPageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    patchSearchParams({ list_page: String(value), tab: MAIN_TABS.LISTS });
    window.scrollTo(0, 0);
  };

  /**
   * 件数バッジ付きのタブを1つ作る。
   *
   * 0件のタブを淡くするのは**他人のプロフィールだけ**。自分のページで
   * 自分の未着手のタブが淡いのは、機能が使えないように見えて損なだけ。
   * 淡くはしても押せなくはしない（理由は ProfileTabLabel.tsx）。
   *
   * `Tabs` は子から選択中の位置を決めるので、**必ず `Tab` を直接返すこと**
   * （間に別の要素を挟むと下線の位置がずれる）。
   */
  const countedTab = (tab: MainTabType, labelKey: string) => {
    // 淡くするかどうかは実件数で決め、数字として出すかは別に判断する
    // （0 は出さない / フォローは数を出さない。ProfileTabLabel 参照）
    const dimmed = isPublic && tabCountFor(tab, mediaType, profile) === 0;
    return (
      <Tab
        key={tab}
        value={tab}
        label={
          <ProfileTabLabel
            label={t(labelKey)}
            count={tabBadgeCount(tab, mediaType, profile, showSampleTabCounts)}
            accent={accentBold}
          />
        }
        sx={dimmed ? { opacity: 0.42 } : undefined}
      />
    );
  };

  // 未ログインで自分のページを開いた場合。**フックを全部通した後に返すこと。**
  if (needsLogin) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  return (
    // アクセント色はプロフィール配下だけに効かせる。
    // 配下の primary.main 参照がそのまま追従するので個別の書き換えが要らない。
    // 対象を広げるときはこの ThemeProvider の位置を外へ動かす。
    // **段階(ProfileAccentLevelContext)も対で動かすこと。** 色だけ広げると
    // 「強め」でしか出さない要素が既定の見た目のまま取り残される。
    <ThemeProvider theme={profileTheme}>
    <ProfileAccentLevelContext.Provider value={accentLevel}>
    <ProfileCardScoreAccentContext.Provider value={cardScoreAccent === 'a'}>
    {/* カードのホバー演出(枠線・光沢)はこの配下だけ。外は拡大のみ。
        display:contents なのでレイアウトには効かない。 */}
    <Box className={CARD_FX_SCOPE_CLASS} sx={{ display: 'contents' }}>
      {ambient && <AmbientBackground preset={ambient} accent={accent} />}
      <Helmet>
        <title>
          {isPublic && profile
            ? `${profile.display_name || profile.username}(@${profile.username})のゲームライブラリ | ${SITE_NAME}`
            : `マイページ | ${SITE_NAME}`}
        </title>
      </Helmet>

      <Grid container spacing={3} sx={{ mb: { xs: 2, md: 2 } }}>
        <Grid item xs={12}>
          {/* SP だけ、AppBar の裏に同じ絵のぼかしを敷いて色を画面最上部まで届かせる。
              PC はバナー自身が潜り込むので不要（敷くと二重になる）。
              **読み込み中も出すこと。** AppBar は透過のままなので、
              ここを分岐の内側に入れると読み込み中だけ上端が素の暗色になる。
              出し方(revealed)はバナー本体と揃えること。揃えないと本体がフェード中に
              画面最上部だけ先に色が変わる。 */}
          {isCompactHeader && (
            <BannerTopWash
              background={bannerPaintedBase}
              revealed={bannerRevealed}
              height={LAYOUT_TOP_PAD.xs}
            />
          )}

          {isError ? (
            <Box sx={{ py: 8, textAlign: 'center' }}>
              <Typography variant="h6" sx={{ mb: 1 }}>プロフィールを読み込めませんでした</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                通信に失敗した可能性があります。
              </Typography>
              <StyledButton onClick={() => profileCache.invalidate()}>再読み込み</StyledButton>
            </Box>
          ) : !profile ? (
            // 寸法は本体と共有(headerLayout)。切り替わりで下のコンテンツが飛ばない。
            // 自分のページの切替バーは端末設定だけで有無が決まるので先に場所を取る
            <ProfileHeaderSkeleton reserveMediaBar={!isPublic && !extBarHidden} />
          ) : (
            <>
              {/* バナー。画像未設定のときはユーザー名／アクセント由来のグラデーション。
                  mt の負値は PC では AppBar の下へ潜り込ませ、SP では Container の
                  余白だけ打ち消して AppBar の直下から始める（BANNER_PULL_UP 参照）。
                  mx:-1.5 は Container の px:1.5 を打ち消して左右いっぱいに広げるため。 */}
              <Box sx={{ position: 'relative', mt: BANNER_PULL_UP, mx: BANNER_BLEED_MX }}>
                <Box sx={{
                  position: 'relative',
                  height: banner.height,
                  overflow: 'hidden',
                  maskImage: BANNER_MASK_SX,
                  WebkitMaskImage: BANNER_MASK_SX,
                }}>
                  {/* フェードインの下地。無彩色。上の層が透けているあいだ見えているのはこれ */}
                  <Box sx={{ position: 'absolute', inset: 0, background: NEUTRAL_BANNER_BASE }} />
                  {/* 下地。グリッチの帯はこの上に同じ絵を重ねてずらす。
                      スローズームはこの層そのものを動かすので、層は増えない。
                      **opacity で出すこと。** background を差し替える形にすると
                      画像とグラデーションの間はトランジションが効かず、パッと切り替わる。 */}
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      background: bannerPaintedBase,
                      opacity: bannerRevealed ? 1 : 0,
                      transition: 'opacity 0.4s ease',
                      ...(bannerAnimating && bannerKind === 'zoom'
                        ? bannerZoomSx(bannerEffectConfig as ZoomParams)
                        : {}),
                    }}
                  />
                  {bannerAnimating && bannerKind === 'glitch' && (
                    <BannerGlitch background={bannerPaintedBase} params={bannerEffectConfig as GlitchParams} />
                  )}
                  {bannerAnimating && bannerKind === 'sweep' && (
                    <BannerSweep params={bannerEffectConfig as SweepParams} accent={accent} />
                  )}
                  {/* 文字を読ませる層。エフェクトの上に置くこと。
                      帯が動く上に乗るので、昇格させないと帯が飛ぶたびに
                      1120x420 のグラデーションを塗り直すことになる。 */}
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      pointerEvents: 'none',
                      background: bannerOverlaySx(bannerImageUrl),
                      ...(bannerAnimating && { willChange: 'transform' }),
                    }}
                  />
                </Box>
                {!isPublic && (
                  <>
                    {/* 入力の実体。導線は2つあり、どちらもこれを叩く。
                        「…」メニュー（常時）と、デザインパネルを開いている間の
                        ヘッダー上のボタン（下記）。 */}
                    <input
                      ref={bannerInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      onChange={handleBannerFile}
                      style={{ display: 'none' }}
                    />
                    {/* プレビュー中の確定/取り消し。本番と同じ枠に当てた状態で選ばせる。
                        AppBar と衝突しない位置(上端から少し下)に置く。
                        xs の 56 は右上のユーティリティ群(top:10 / 高さ24)を避けるため。 */}
                    {pendingBanner && !bannerUploading && (
                      <Box sx={{
                        position: 'absolute',
                        top: { xs: 56, sm: 64, md: 80 },
                        left: 0,
                        right: 0,
                        display: 'flex',
                        justifyContent: 'center',
                        gap: 1,
                      }}>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={handleBannerSave}
                          sx={{ borderRadius: '50px' }}
                        >
                          この画像で保存
                        </Button>
                        {/* **「別の画像」は置かない。** 取り消してから選び直せばよく、
                            1手増えるだけ。選択肢が3つ並ぶほうが押す場所を迷う。
                            アイコン側も同じ方針（取り消しを挟む）で揃えてある。 */}
                        <Button
                          size="small"
                          onClick={handleBannerCancel}
                          sx={{ ...GLASS_ACTION_SX, borderRadius: '50px', px: 2 }}
                        >
                          取り消す
                        </Button>
                      </Box>
                    )}
                    {/* デザインパネルを開いている間だけ出す画像の導線。
                        「…」メニューだけだとヘッダー画像をどこで変えるのか分からない。
                        **覆いや veil は入れないこと。** パネルは効果を見ながら選ぶための
                        ものなので、ヘッダーを暗くしたらその目的が消える。
                        置き場所はプレビュー中の確定ボタンと同じ（同時には出ない）。 */}
                    {customizeOpen && !pendingBanner && !bannerUploading && (
                      <Box sx={{
                        position: 'absolute',
                        top: { xs: 56, sm: 64, md: 80 },
                        left: 0,
                        right: 0,
                        display: 'flex',
                        justifyContent: 'center',
                        gap: 1,
                      }}>
                        <Button
                          size="small"
                          startIcon={<ImageOutlinedIcon />}
                          onClick={() => bannerInputRef.current?.click()}
                          sx={{ ...GLASS_ACTION_SX, borderRadius: '50px', px: 2 }}
                        >
                          {profile.banner ? 'ヘッダー画像を変更' : 'ヘッダー画像を設定'}
                        </Button>
                        {profile.banner && (
                          <Button
                            size="small"
                            onClick={handleBannerDelete}
                            sx={{ ...GLASS_ACTION_SX, borderRadius: '50px', px: 2 }}
                          >
                            削除
                          </Button>
                        )}
                      </Box>
                    )}
                    {bannerUploading && (
                      <Box sx={{
                        position: 'absolute',
                        inset: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}>
                        <CircularProgress size={28} />
                      </Box>
                    )}
                  </>
                )}
                {/* SP 専用のユーティリティ群。詳細は ownerActions のコメント参照。
                    **PC では絶対にここへ出さないこと**(バナーが AppBar の裏に潜るため)。
                    下の「ヘッダー画像を変更」帯とは縦に重ならないよう、
                    あちらを xs だけ 56px へ下げてある。 */}
                {!isPublic && !ownerActionsInBottomRow && (
                  <Box sx={{
                    position: 'absolute',
                    top: 10,
                    right: 12,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                  }}>
                    {ownerActions}
                  </Box>
                )}
                {/* バナー下端の1行。アバター〜名前と操作を同じ flex に入れることで、
                    要素同士が重ならない＝クリックを奪い合わない。
                    以前はアバター行を負マージンでバナーに食い込ませていたため、
                    後続要素がバナー下端を覆って操作ボタンが押せなくなっていた。 */}
                <Box sx={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  // バナー下端そのものではなく、BANNER_EXTRA_DEPTH だけ上に置く。
                  // バナーはこの行より下へ伸びて紹介文の裏に潜り込む。
                  bottom: banner.extraDepth,
                  // 横は Container の px:1.5(=12px) に合わせること。バナーは mx:-1.5 で
                  // 全幅に広げてあるので、ここを 12px にして初めてアバターの左端が
                  // 下に続くコンテンツの左端と揃う。縦は行の位置を動かさないよう据え置き。
                  px: BANNER_ROW_PX,
                  py: BANNER_ROW_PY,
                  display: 'flex',
                  alignItems: 'flex-end',
                  gap: { xs: 1.5, md: 2 },
                }}>
                  {/* 装飾パネルを開いている間だけ、アイコンをその場で差し替えられる。
                      縁取りの見え方は実物のアイコンに乗せて初めて分かるので、
                      設定画面へ往復させない。送り先は専用エンドポイントで
                      プロフィール本体の PATCH を通らないが、**確定するのは
                      パネルの保存ボタン**（saveDisplay がまとめて送る）。 */}
                  <Box sx={{ position: 'relative', flexShrink: 0 }}>
                    <AvatarRing
                      src={pendingAvatar?.url ?? profile.avatar ?? undefined}
                      alt={profile.display_name || profile.username}
                      size={AVATAR_SIZE}
                      borderKey={avatarBorder}
                      widthKey={avatarBorderWidth}
                      accent={accent}
                    />
                    {/* 所有者判定は `!isPublic`。**isOwnProfile を使わないこと。**
                        あれは PublicProfilePage からしか渡らず、「自分のプロフィールを
                        公開ビューで見ているか」を表す。自分のページ(/profile)は
                        props 無しで描画されるので常に false になる。 */}
                    {/* **選択後は「取り消し」に変わる。差し替えボタンは作らない。**
                        3つ目のボタンを足すより、取り消してから選び直すほうが
                        押す場所が1つで済む（1手増えるだけで、迷う余地が消える）。
                        保存はパネルの保存ボタンが兼ねる。 */}
                    {!isPublic && customizeOpen && (
                      <Tooltip title={pendingAvatar ? '選択を取り消す' : 'アイコンを変更'} arrow>
                        <IconButton
                          size="small"
                          disabled={avatarUploading || displaySaving}
                          onClick={pendingAvatar ? handleAvatarCancel : () => avatarInputRef.current?.click()}
                          sx={{ position: 'absolute', right: -2, bottom: -2, ...GLASS_ACTION_SX }}
                        >
                          {avatarUploading
                            ? <CircularProgress size={14} color="inherit" />
                            : pendingAvatar
                              ? <CloseIcon sx={{ fontSize: '0.9rem' }} />
                              : <ImageOutlinedIcon sx={{ fontSize: '0.9rem' }} />}
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                  <input
                    ref={avatarInputRef}
                    type="file"
                    accept="image/jpeg,image/png"
                    hidden
                    onChange={handleAvatarFile}
                  />
                  {/* **確定/取り消しをこの行に置かないこと。**
                      ここはアバターと名前カラムが幅を取り合う flex 行で、
                      ボタンを挟むと名前カラムが潰れてレイアウトが崩れる
                      （375px では致命的）。「見ながら決める」ための機能が
                      見ている対象を壊すことになる。操作は装飾パネル側に置いてある。 */}
                  {/* **アバターに対して中央揃えにすること(下端揃えにしない)。**
                      行の高さはアバターで決まるので、下端揃えだとカラムが短いぶんが
                      まるごと上の余白になる。ID の下(ソーシャル / 場所)が空の人は
                      2行=36px しかなく、文字ブロックの光学中心がアバター中心より
                      18px(xs) / 24px(md) 下にぶら下がって見える。
                      3行あるときの見え方は中央揃えでもほぼ変わらない(4px 上がるだけ)。 */}
                  <Box sx={{ flex: 1, minWidth: 0, alignSelf: 'center' }}>
                    {/* 名前は省略せず折り返す。溢れたら先に落ちるのはバッジ(flexWrap)。
                        mb は rowGap と同値にすること(バッジが2行目に落ちたとき @ID と等間隔になる)。
                        mr は名前行だけ。右カラムは下端の1行なので名前行とは縦で当たらず、
                        その幅を負マージンで取り返す(375px で 139px → 267px)。
                        @ID / ソーシャル行は右カラムと同じ高さなので伸ばすと潜り込む。 */}
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      columnGap: 0.75,
                      rowGap: 0.5,
                      mb: 0.5,
                      minWidth: 0,
                      mr: {
                        xs: `${-(bannerActionsWidth + 12)}px`,
                        md: `${-(bannerActionsWidth + 16)}px`,
                      },
                    }}>
                      <Typography
                        variant="h6"
                        component="h1"
                        sx={{
                          fontSize: { xs: '0.8rem', md: '1.1rem' },
                          fontWeight: 600,
                          lineHeight: 1.1,
                          minWidth: 0,
                          overflowWrap: 'anywhere',
                        }}
                      >
                        {profile.display_name || profile.username}
                      </Typography>
                      <UserPlanBadge planType={profile.subscription_plan} sx={{ flexShrink: 0 }} />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                      <Typography variant="body2" color="text.secondary" noWrap sx={{ minWidth: 0 }}>
                        @{profile.username}
                      </Typography>
                      {isPublic && profile.last_access && !profile.current_playing_game_id && !steamStatus?.gameextrainfo && (
                        // 状態チップは縮めず、溢れは @ID 側の省略で吸収する
                        <Box sx={{ flexShrink: 0, display: 'inline-flex' }}>
                          <OnlineStatus lastAccess={profile.last_access} />
                        </Box>
                      )}
                      {/* Steamでプレイ中のゲーム表示 */}
                      {steamStatus?.gameextrainfo && !profile.current_playing_game_id && (
                        <Box
                          onClick={handleSteamGameClick}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            flexShrink: 0,
                            backgroundColor: 'rgba(102, 192, 244, 0.12)',
                            border: '1px solid rgba(102, 192, 244, 0.5)',
                            borderRadius: '4px',
                            padding: '0px 8px',
                            paddingLeft: '4px',
                            textDecoration: 'none',
                            position: 'relative',
                            overflow: 'hidden',
                            cursor: steamStatus.gameid ? 'pointer' : 'default',
                            '&:hover': {
                              opacity: steamStatus.gameid ? 0.8 : 1
                            },
                            '&::after': {
                              content: '""',
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              background: 'linear-gradient(90deg, rgba(102, 192, 244, 0), rgba(102, 192, 244, 0.2), rgba(102, 192, 244, 0))',
                              backgroundSize: '200% 100%',
                              animation: 'shimmer 2s infinite',
                            },
                            '@keyframes shimmer': {
                              '0%': {
                                backgroundPosition: '100% 0'
                              },
                              '100%': {
                                backgroundPosition: '-100% 0'
                              }
                            }
                          }}
                        >
                          <Box
                            sx={{
                              width: '0.85rem',
                              height: '0.85rem',
                              maskImage: 'url(/icons/service/steam_black.svg)',
                              maskSize: 'contain',
                              maskRepeat: 'no-repeat',
                              maskPosition: 'center',
                              WebkitMaskImage: 'url(/icons/service/steam_black.svg)',
                              WebkitMaskSize: 'contain',
                              WebkitMaskRepeat: 'no-repeat',
                              WebkitMaskPosition: 'center',
                              backgroundColor: '#e6e6e6',
                              zIndex: 1,
                            }}
                          />
                          <Typography
                            variant="caption"
                            sx={{
                              color: '#e6e6e6',
                              fontSize: '0.7rem',
                              fontWeight: 400,
                              lineHeight: '1',
                              letterSpacing: '0.02em',
                              // モバイルは名前カラム自体が 139px 前後しかないので、
                              // ゲーム名にそれ以上取らせない
                              maxWidth: { xs: '100px', md: '180px' },
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              zIndex: 1,
                            }}
                          >
                            {steamStatus.gameextrainfo}
                          </Typography>
                        </Box>
                      )}
                      {profile.current_playing_game_id && (
                        <Box
                          onClick={() => navigate(`/game/${profile.current_playing_game_id}`)}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            flexShrink: 0,
                            backgroundColor: 'rgba(217, 255, 0, 0.12)',
                            border: '1px solid rgba(217, 255, 0, 0.5)',
                            borderRadius: '4px',
                            padding: '0px 8px',
                            paddingLeft: '4px',
                            position: 'relative',
                            overflow: 'hidden',
                            cursor: 'pointer',
                            '&:hover': {
                              opacity: 0.8
                            },
                            '&::after': {
                              content: '""',
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              background: 'linear-gradient(90deg, rgba(217, 255, 0, 0), rgba(217, 255, 0, 0.2), rgba(217, 255, 0, 0))',
                              backgroundSize: '200% 100%',
                              animation: 'shimmer 2s infinite',
                            },
                            '@keyframes shimmer': {
                              '0%': {
                                backgroundPosition: '100% 0'
                              },
                              '100%': {
                                backgroundPosition: '-100% 0'
                              }
                            }
                          }}
                        >
                          <PlayArrowIcon
                            sx={{
                              fontSize: '0.875rem',
                              color: '#d9ff00',
                            }}
                          />
                          <Typography
                            variant="caption"
                            sx={{
                              color: '#d9ff00',
                              fontSize: '0.7rem',
                              fontWeight: 400,
                              lineHeight: '1',
                              letterSpacing: '0.02em',
                              zIndex: 1
                            }}
                          >
                            now playing
                          </Typography>
                        </Box>
                      )}
                    </Box>
                    {/* ソーシャルリンクは本文側ではなくバナー内(名前の直下)に置く。
                        **場所タグもここに同居させる（PC / SP 共通）。**
                        SP でも成立するのはユーティリティ群を右上へ逃がして
                        名前カラムが 259px 取れるようになったため。ここを戻すなら
                        場所タグも紹介文側へ戻すこと。
                        SNS 未設定 + 場所だけ、という組み合わせでも消えないよう
                        条件は「どちらか有れば」にしてある。 */}
                    {(hasSocialLinks || profile.location || showSampleSocial) && (
                      <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                        {hasSocialLinks && (
                          <SocialLinks
                            socialLinks={profile.social_links!}
                            iconOnly={isNarrowHeader}
                            accentIcons={accentBold}
                            onCopy={notify}
                          />
                        )}
                        {/* SNS 未設定の人に、装飾パネルを開いている間だけ見本を出す。
                            「強め」が色を乗せる先はこのアイコンなので、
                            無いままだと切り替えても何も起きない。中身はダミーで、
                            押せないようにしてある（SocialLinks の sample）。 */}
                        {showSampleSocial && (
                          <SocialLinks
                            socialLinks={SAMPLE_SOCIAL_LINKS}
                            iconOnly={isNarrowHeader}
                            accentIcons={accentBold}
                            sample
                          />
                        )}
                        {profile.location && wrapWithLocationDialog(
                          <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            minWidth: 0,
                            color: 'text.secondary',
                            // 切れているときだけ「押せる」ことを示す。
                            // 切れていなければ開いても得るものが無いので何も出さない。
                            ...(locationTruncated && {
                              cursor: 'pointer',
                              '&:hover': { color: 'text.primary' },
                            }),
                          }}>
                            <FmdGoodOutlinedIcon sx={{ fontSize: '0.95rem', flexShrink: 0 }} />
                            <Typography ref={locationRef} variant="body2" noWrap sx={{ fontSize: '0.75rem' }}>
                              {profile.location}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    )}
                  </Box>
                  {/* 名前ブロックと同じ flex 行に入れることで、絶対配置どうしが
                      重なってクリックを奪う事故を防いでいる。 */}
                  {bannerActions}
                </Box>
              </Box>
              {/* SocialLinks はバナー内(名前の直下)へ移動済み */}
              {/* バナー下端に少し乗り上げる。後続の要素なのでバナーより上に描画される。
                  Tips で包むとラッパーが1枚挟まるので、乗り上げは外側のこの Box が持つ。 */}
              <Box sx={{ mt: banner.bubbleOverlap }}>
              {profile.bio && wrapWithBioDialog(
                <Box sx={{
                  position: 'relative',
                  ...(bioTruncated && {
                    cursor: 'pointer',
                    transition: 'background-color 0.15s',
                    '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.06)' },
                  }),
                  // 行長を切らないと、2〜3行の文章が横幅いっぱいに伸びて宙に浮いて見える
                  maxWidth: { md: '46em' },
                  p: { xs: 1.5, md: 2 },
                  borderRadius: 3,
                  ...bubbleSurface(accentBold ? accent : null),
                  backdropFilter: 'blur(10px)',
                  WebkitBackdropFilter: 'blur(10px)',
                }}>
                  {/* クランプは必ず本文と同じ要素に掛けること。
                      外側の Box に置くと em の基準がその Box の font-size になり、
                      テーマの body2(0.8rem / モバイル 0.7rem)とズレて半端な行数で切れる。
                      ここに置けば em = body2 の実サイズなので、どの画面幅でも正確に3行になる。 */}
                  <Typography
                    ref={bioRef}
                    component="div"
                    variant="body2"
                    sx={{
                      lineHeight: 1.35,
                      fontSize: '0.8rem',
                      color: '#ffffffd8',
                      // 3行で切って末尾に省略記号を出す。
                      // maxHeight は line-clamp と同じ3行ぶんに揃えておく
                      // (line-clamp だけだと一部ブラウザで scrollHeight が
                      //  clientHeight と一致して溢れ判定が効かなくなるため保険)。
                      display: '-webkit-box',
                      WebkitBoxOrient: 'vertical',
                      WebkitLineClamp: 3,
                      maxHeight: '4.05em', // = lineHeight 1.35 × 3
                      overflow: 'hidden',
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {profile.bio}
                  </Typography>
                  {/* 開けることの合図。行を増やさないよう本文の上に重ねる。
                      切れていること自体は末尾の省略記号が示すので、ここは操作の的だけを置く。
                      円形＋枠で「押せるもの」に見せる(色は使わない)。 */}
                  {bioTruncated && (
                    <Box sx={{
                      position: 'absolute',
                      right: { xs: 8, md: 12 },
                      bottom: { xs: 8, md: 12 },
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 26,
                      height: 26,
                      borderRadius: '50%',
                      color: 'text.secondary',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      border: '1px solid rgba(255, 255, 255, 0.16)',
                      backdropFilter: 'blur(4px)',
                      WebkitBackdropFilter: 'blur(4px)',
                    }}>
                      <ExpandMoreIcon sx={{ fontSize: '1.1rem' }} />
                    </Box>
                  )}
                  {/* 場所タグはバナーの SOCIAL 行へ移動済み(PC / SP 共通) */}
                </Box>
              )}

              {/*
                紹介文が空の人に、装飾パネルを開いている間だけ面を見せる。

                **これが無いと「アクセントスタイル」が何も説明できない。**
                強めが色を乗せる先は紹介文の面と SNS アイコンなので、
                どちらも未設定の登録したての人が切り替えても画面は一切変わらず、
                設定が壊れているようにしか見えなかった。

                中身は入れず、**書き足す場所であることと入口だけ**を示す。
                ここから編集させないのは、紹介文と SOCIAL がプロフィール設定の
                フォーム側（誕生日必須を含む一式）に属するため。
                アイコンだけ直接差し替えられるのは、保存経路が独立しているから。
              */}
              {previewEmpty && !profile.bio && (
                <Box sx={{
                  maxWidth: { md: '46em' },
                  p: { xs: 1.5, md: 2 },
                  borderRadius: 3,
                  ...bubbleSurface(accentBold ? accent : null),
                  backdropFilter: 'blur(10px)',
                  WebkitBackdropFilter: 'blur(10px)',
                }}>
                  {/* 設定への導線は付けない。文言で場所は分かるし、
                      ここはあくまで「面の見え方を確かめる場所」で操作の場ではない */}
                  <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)' }}>
                    紹介文とソーシャルアカウントは設定＞プロフィールから追加できます
                  </Typography>
                </Box>
              )}
              </Box>

              {/* 「…」に残すのはヘッダー画像の操作とインストールだけ。
                  見た目・共有・設定は独立したアイコンで常時出している(bannerActions)。 */}
              <Menu
                anchorEl={headerMenuAnchor}
                open={Boolean(headerMenuAnchor)}
                onClose={() => setHeaderMenuAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <MenuItem
                  disabled={bannerUploading}
                  onClick={() => { setHeaderMenuAnchor(null); bannerInputRef.current?.click(); }}
                >
                  <ListItemIcon><ImageOutlinedIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primaryTypographyProps={{ variant: 'body2' }}>
                    {profile.banner ? 'ヘッダー画像を変更' : 'ヘッダー画像を設定'}
                  </ListItemText>
                </MenuItem>
                {profile.banner && (
                  <MenuItem disabled={bannerUploading} onClick={handleBannerDelete}>
                    <ListItemIcon><DeleteOutlineIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primaryTypographyProps={{ variant: 'body2' }}>ヘッダー画像を削除</ListItemText>
                  </MenuItem>
                )}
                {!isPwa() && [
                  <Divider key="pwa-divider" />,
                  // PWA は強く勧めたいので、他の項目と同じ見え方にしない
                  <MenuItem
                    key="pwa"
                    onClick={() => { setHeaderMenuAnchor(null); navigate('/install-pwa'); }}
                    sx={{ color: 'primary.main', fontWeight: 600 }}
                  >
                    <ListItemIcon><GetAppIcon fontSize="small" sx={{ color: 'primary.main' }} /></ListItemIcon>
                    <ListItemText primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}>
                      アプリをインストール
                    </ListItemText>
                  </MenuItem>,
                ]}
              </Menu>

              {/* 共有はバナー上の独立アイコンから。挙動は ShareButtons と同じに保つこと */}
              <Menu
                anchorEl={shareMenuAnchor}
                open={Boolean(shareMenuAnchor)}
                onClose={() => setShareMenuAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <MenuItem onClick={() => { setShareMenuAnchor(null); handleShareX(profile); }}>
                  <ListItemIcon><XIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primaryTypographyProps={{ variant: 'body2' }}>Xでシェア</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => { setShareMenuAnchor(null); handleCopyProfileUrl(profile); }}>
                  <ListItemIcon><ContentCopyIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primaryTypographyProps={{ variant: 'body2' }}>URLをコピー</ListItemText>
                </MenuItem>
                <MenuItem
                  component="a"
                  href={`/profile/${profile.username}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setShareMenuAnchor(null)}
                >
                  <ListItemIcon><OpenInNewIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primaryTypographyProps={{ variant: 'body2' }}>公開ページを開く</ListItemText>
                </MenuItem>
              </Menu>
            </>
          )}
        </Grid>
      </Grid>

      {/* ここから下はプロフィールが確定してから。
          以前は読み込み中でもタブ列とタブの中身を描いていたので、
          username 未確定のまま各タブがマウントされ、空の面が一瞬見えていた。 */}
      {profile && (
        <>
          {/* メディアタイプ切替。ⓘ は拡張ライブラリ未使用のときだけ（＝説明が要る人にだけ）出す。 */}
          {showMediaBar && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <MediaSegmentedControl value={mediaType} onChange={handleMediaChange} counts={mediaCounts} />
              {!isPublic && !hasExtData && (
                <Tooltip title={t('extLibrary.iconTooltip')}>
                  <IconButton
                    size="small"
                    onClick={() => setExtInfoOpen(true)}
                    sx={{
                      mt: -1,
                      mb: 1.5,
                      // 幅が足りないときに縮むのは切替バー側(横スクロールする)。
                      // ここが潰れるとアイコンが歪む。
                      flexShrink: 0,
                      color: 'rgba(255, 255, 255, 0.4)',
                      '&:hover': { color: 'rgba(255, 255, 255, 0.7)' },
                    }}
                  >
                    <InfoOutlinedIcon sx={{ fontSize: '1rem' }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          )}

          {/* メインタブ */}
          <Box ref={tabsBoxRef} sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs
              value={mainTabValue}
              onChange={handleMainTabChange}
              variant="scrollable"
              scrollButtons={false}
              sx={{
                minHeight: { xs: '32px', sm: '40px' },
                // スクロールできる側だけを透過させ、続きがあることを示す。
                // 下線(borderBottom)は親のBoxにあるのでフェードの影響を受けない。
                maskImage: tabFadeMask,
                WebkitMaskImage: tabFadeMask,
                '& .MuiTab-root': {
                  minHeight: { xs: '36px', sm: '40px' },
                  py: { xs: 1, sm: 1 },
                  px: 1,
                  fontSize: { xs: '0.65rem', sm: '0.75rem' },
                  minWidth: 0
                }
              }}
            >
              {/* 出せるタブの判定は resolveTab と同じ isTabAvailable を通すこと。
                  片方だけ変えると「選択中なのに下線が無いタブ」が生まれる。 */}
              {isTabAvailable(MAIN_TABS.TIMELINE, mediaType, isPublic) && (
                <Tab
                  icon={
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 24,
                        height: 24,
                        borderRadius: '4px',
                        backgroundColor: 'rgba(255, 255, 255, 0.08)',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.12)'
                        }
                      }}
                    >
                      <BoltIcon
                        sx={{
                          fontSize: '1rem',
                          transform: 'rotate(20deg)'
                        }}
                      />
                    </Box>
                  }
                  value={MAIN_TABS.TIMELINE}
                />
              )}
              {countedTab(MAIN_TABS.PROFILE, 'tabs.profile')}
              {countedTab(MAIN_TABS.LIBRARY, 'tabs.library')}
              {countedTab(MAIN_TABS.REVIEWS, 'tabs.reviews')}
              {mediaType === 'game' && countedTab(MAIN_TABS.POSTS, 'tabs.posts')}
              {mediaType === 'game' && countedTab(MAIN_TABS.LISTS, 'tabs.lists')}
              {mediaType === 'game' && countedTab(MAIN_TABS.GALLERY, 'tabs.gallery')}
              {mediaType === 'game' && countedTab(MAIN_TABS.MEMORIES, 'tabs.memories')}
              {mediaType === 'game' && countedTab(MAIN_TABS.PLAYLOGS, 'tabs.playlogs')}
              {mediaType === 'game' && countedTab(MAIN_TABS.GOTY, 'tabs.goty')}
              {mediaType === 'game' && countedTab(MAIN_TABS.ACTIVITY, 'tabs.activity')}
              {mediaType === 'game' && countedTab(MAIN_TABS.FOLLOWS, 'tabs.follows')}
            </Tabs>
          </Box>

          {/* タブの中身。**全部 lazy なので Suspense で1つ包む。**
              個別に包むと切替のたびに fallback の高さが変わって画面が跳ねる。

              **key はユーザー単位で付けること。**
              ページ全体の `key={username}` は外した（キャッシュが効いていても
              ヘッダーごと作り直しになるため）が、タブの中身は別で、
              各タブが username に紐づく状態を内部に持っている
              (ProfileTab の統計、GameLibraryTab のステータス/ページ/フォルダ、
               選択モードなど)。ここを共有すると、既に見たことのある人へ移った
              ときだけ前の人の状態が残る。**ヘッダーで潰した「前の人が残る」を
              タブの中で再発させないこと。** */}
          <Suspense key={username ?? 'me'} fallback={<TabFallback />}>
            {mainTabValue === MAIN_TABS.LIBRARY && (
              <Box sx={{ width: '100%' }}>
                {mediaType === 'game' && (
                  <GameLibraryTab
                    profile={profile}
                    isPublic={isPublic}
                    gameCounts={gameCounts}
                    mainTabValue={mainTabValue}
                  />
                )}
                {mediaType === 'anime' && (
                  <AnimeLibraryTab
                    profile={profile}
                    isPublic={isPublic}
                    animeCounts={animeCounts}
                    mainTabValue={mainTabValue}
                  />
                )}
                {mediaType === 'movie' && (
                  <MovieLibraryTab
                    profile={profile}
                    isPublic={isPublic}
                    movieCounts={movieCounts}
                    mainTabValue={mainTabValue}
                  />
                )}
              </Box>
            )}

            {mainTabValue === MAIN_TABS.LISTS && (
              <Box>
                {!isPublic && (
                  <StyledButton onClick={openCreateList} startIcon={<PlaylistAddIcon />} sx={{ mb: 2 }}>
                    新規リストを作成
                  </StyledButton>
                )}

                <Grid container spacing={{ xs: 0.5, sm: 1, md: 1 }}>
                  {gameLists.map((list) => (
                    <GameListComponent
                      key={list.id}
                      list={list}
                      onEdit={!isPublic ? handleEditList : undefined}
                      onDelete={!isPublic ? handleDeleteList : undefined}
                    />
                  ))}
                </Grid>

                {listTotalPages > 1 && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <Pagination
                      count={listTotalPages}
                      page={listPage}
                      onChange={handleListPageChange}
                    />
                  </Box>
                )}

                {/* ダイアログは開くまで読み込まない(Lexical を初期チャンクから外すため)。
                    open は素直に渡すこと。閉じるアニメーションが要るので、
                    一度開いたら閉じてもマウントは外さない(createListMounted 参照)。 */}
                {createListMounted && (
                  <CreateGameList
                    open={createListDialogOpen}
                    onClose={() => setCreateListDialogOpen(false)}
                    onListCreated={invalidateGameLists}
                  />
                )}

                {editingList && (
                  <EditGameList
                    open
                    onClose={() => setEditingList(null)}
                    list={editingList}
                    onListUpdated={invalidateGameLists}
                  />
                )}
              </Box>
            )}

            {mainTabValue === MAIN_TABS.GOTY && (
              <GotyTab username={profile.username} isPublic={isPublic} />
            )}

            {mainTabValue === MAIN_TABS.REVIEWS && (
              mediaType === 'anime' ? (
                <AnimeReviewsTab
                  username={profile.username}
                  displayName={profile.display_name}
                  isPublic={isPublic}
                />
              ) : mediaType === 'movie' ? (
                <MovieReviewsTab
                  username={profile.username}
                  displayName={profile.display_name}
                  isPublic={isPublic}
                />
              ) : (
                <ReviewsTab
                  username={profile.username}
                  displayName={profile.display_name}
                  isPublic={isPublic}
                />
              )
            )}

            {mainTabValue === MAIN_TABS.MEMORIES && (
              <MemoriesTab
                username={profile.username}
                isPublic={isPublic}
                userProfile={profile}
              />
            )}

            {mainTabValue === MAIN_TABS.POSTS && (
              <Grid container spacing={2}>
                <Grid item xs={12} sm={12} md={8.5}>
                  <PostFeed
                    username={profile.username}
                    isMyPage={!isPublic}
                    avatar={profile.avatar}
                    displayName={profile.display_name}
                    autoFocusComposer={searchParams.get('compose') === '1'}
                  />
                </Grid>
              </Grid>
            )}

            {mainTabValue === MAIN_TABS.ACTIVITY && (
              <ActivityTab username={profile.username} isPublic={isPublic} />
            )}

            {mainTabValue === MAIN_TABS.PROFILE && mediaType === 'game' && (
              <ProfileTab profile={profile} isPublic={isPublic} />
            )}
            {mainTabValue === MAIN_TABS.PROFILE && mediaType === 'anime' && (
              <AnimeProfileTab profile={profile} isPublic={isPublic} />
            )}
            {mainTabValue === MAIN_TABS.PROFILE && mediaType === 'movie' && (
              <MovieProfileTab profile={profile} isPublic={isPublic} />
            )}

            {mainTabValue === MAIN_TABS.FOLLOWS && (
              <FollowsTab username={profile.username} isPublic={isPublic} />
            )}

            {mainTabValue === MAIN_TABS.TIMELINE && (
              <TimelineTab username={profile.username} isPublic={isPublic} />
            )}

            {mainTabValue === MAIN_TABS.PLAYLOGS && (
              <PlayLogsTab username={profile.username} isPublic={isPublic} />
            )}

            {mainTabValue === MAIN_TABS.GALLERY && (
              <GalleryTab username={profile.username} isPublic={isPublic} />
            )}
          </Suspense>
        </>
      )}

      <BaseDialog
        open={extInfoOpen}
        onClose={() => setExtInfoOpen(false)}
        title={t('extLibrary.title')}
        actions={
          <>
            <Button onClick={() => setExtInfoOpen(false)} size="small" sx={{ color: 'text.secondary' }}>
              {t('extLibrary.close')}
            </Button>
            <Button onClick={handleHideExtBar} size="small" color="error">
              {t('extLibrary.hide')}
            </Button>
          </>
        }
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {t('extLibrary.body', { site: SITE_NAME })}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('extLibrary.hideNote')}
          </Typography>
        </Box>
      </BaseDialog>

      {/* 画面下固定。ヘッダーを覆わないので、変えながら結果を見られる。
          パネルの中身ごと遅延読み込みなので、開くまでチャンクを取りに行かない。 */}
      {customizeOpen && !isPublic && profile && (
        <Suspense fallback={null}>
          <ProfileCustomizeSheet
            profile={profile}
            accent={accent}
            accentKey={accentKey}
            accentLevel={accentLevel}
            cardScoreAccent={cardScoreAccent}
            ambientKey={ambientKey}
            avatarBorder={avatarBorder}
            avatarBorderWidth={avatarBorderWidth}
            bannerEffect={bannerEffect}
            blocked={blockedDisplay}
            /* アイコンを選んだだけでも保存の対象。含めないと保存ボタンが
               押せないまま「保存されない画像」が表示され続ける */
            dirty={displayDirty || !!pendingAvatar}
            saving={displaySaving}
            isDefault={Object.keys(draftDisplay ?? {}).length === 0}
            onAccentChange={handleAccentChange}
            onAccentCustomChange={handleAccentCustomChange}
            savedCustomHex={savedDisplay?.ac === CUSTOM_ACCENT_KEY ? savedDisplay.acx : undefined}
            onAccentLevelChange={handleAccentLevelChange}
            onCardScoreAccentChange={handleCardScoreAccentChange}
            onAmbientChange={handleAmbientChange}
            onAvatarBorderChange={handleAvatarBorderChange}
            onAvatarBorderWidthChange={handleAvatarBorderWidthChange}
            onBannerEffectChange={handleBannerEffectChange}
            onClose={closeCustomize}
            onSave={saveDisplay}
            onReset={resetDisplay}
            onResetDefault={resetDisplayToDefault}
            onUpgrade={() => navigate('/pricing')}
          />
        </Suspense>
      )}

      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={() => setOpenSnackbar(false)}
        message={snackbarMessage}
      />

    </Box>
    </ProfileCardScoreAccentContext.Provider>
    </ProfileAccentLevelContext.Provider>
    </ThemeProvider>
  );
};

export default ProfilePage;
